import type { JSX } from 'react';

import { Badge, type BadgeProps } from '@/components/ui/badge.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import type {
  AnswerJudgement,
  AnswerJudgementChecklistItem,
  AnswerJudgementCriterion,
  AnswerJudgementSummary,
} from '@/lib/api/quality-types.js';

/** Why the answer judge left a case unscored, keyed by `not_scored_reason`. */
const NOT_SCORED_REASONS: Record<string, string> = {
  no_answer: 'The case saved no answer_text',
  judge_error: 'The judge failed',
  invalid_verdict: 'The judge returned an invalid verdict',
  judge_timeout: 'The judge timed out',
  no_capture: "The chat's reply could not be captured",
};

/** Readable names of the five rubric criteria. */
export const CRITERION_NAMES: Record<string, string> = {
  answers_question: 'Answers the question',
  follows_format: 'Follows the format',
  stays_grounded: 'Stays grounded',
  clarity: 'Clarity',
  language_and_tone: 'Language and tone',
};

/** Label and badge colour of each checklist result. */
const CHECKLIST_RESULTS: Record<
  string,
  { label: string; variant: BadgeProps['variant'] }
> = {
  met: { label: 'Met', variant: 'success' },
  not_met: { label: 'Not met', variant: 'warning' },
  unclear: { label: 'Unclear', variant: 'secondary' },
};

/** What the judge read, keyed by `evidence_kind`. `none` has no label: there was nothing to read. */
const EVIDENCE_KINDS: Record<string, string> = {
  capture: 'Captured answer',
  fallback: 'Fallback text',
};

/** The value a lookup map holds for an API key, or undefined for an unknown or inherited key. */
function knownValue<Value>(
  values: Record<string, Value>,
  key: unknown,
): Value | undefined {
  return typeof key === 'string' &&
    Object.prototype.hasOwnProperty.call(values, key)
    ? values[key]
    : undefined;
}

/** A raw API code made readable: `tool_use` → `tool use`. */
function humanize(code: string): string {
  return code.replace(/_/g, ' ');
}

/** A number from a cast field when it lies within the range, else null. NaN never does. */
function numberInRange(
  value: unknown,
  minimum: number,
  maximum: number,
): number | null {
  return typeof value === 'number' && value >= minimum && value <= maximum
    ? value
    : null;
}

/** A whole number from a cast field when it lies within the range, else null. */
export function wholeNumberInRange(
  value: unknown,
  minimum: number,
  maximum = Number.MAX_SAFE_INTEGER,
): number | null {
  return Number.isInteger(value)
    ? numberInRange(value, minimum, maximum)
    : null;
}

/** A rubric average, which the contract keeps between 1 and 5, or null. */
export function rubricAverageFrom(value: unknown): number | null {
  return numberInRange(value, 1, 5);
}

/** A checklist share, which the contract keeps between 0 and 100, or null. */
export function checklistPercentFrom(value: unknown): number | null {
  return numberInRange(value, 0, 100);
}

/** A non-empty string from a cast field, or null. */
export function nonEmptyText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Objects only: a cast list can carry nulls or strings, and rendering must not throw on them. */
export function objectsIn<Item>(list: unknown): Item[] {
  return Array.isArray(list)
    ? list.filter((item): item is Item => !!item && typeof item === 'object')
    : [];
}

/** "4.4/5": a rubric average with one decimal. */
function averageText(average: number): string {
  return `${average.toFixed(1)}/5`;
}

/** "67%": a checklist share rounded to a whole percent. */
export function percentText(percent: number): string {
  return `${Math.round(percent)}%`;
}

/**
 * The case's judgement when the cast field holds one this build can show, else null. A newer
 * server's unknown status reads as no judgement rather than a half-drawn one.
 */
export function readAnswerJudgement(value: unknown): AnswerJudgement | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const judgement = value as AnswerJudgement;
  return judgement.status === 'scored' || judgement.status === 'not_scored'
    ? judgement
    : null;
}

/** Plain-English reason a case was not scored; an unknown code shows as it came. */
export function notScoredText(reason: unknown): string {
  const code = nonEmptyText(reason);
  if (!code) return 'No reason was recorded';
  return knownValue(NOT_SCORED_REASONS, code) ?? code;
}

/**
 * The Answer badge's tooltip: the checklist share met, or why there is none to show. Without a valid
 * share it blames unclear items only when every item is unclear; otherwise the score is unavailable.
 */
function checklistText(judgement: AnswerJudgement): string {
  const percent = checklistPercentFrom(judgement.checklist_percent);
  if (percent !== null) return `Checklist ${percentText(percent)}`;
  const checklist = objectsIn<AnswerJudgementChecklistItem>(
    judgement.checklist,
  );
  if (checklist.length === 0) return 'No checklist';
  return checklist.every((item) => item.result === 'unclear')
    ? 'Checklist: every item unclear'
    : 'Checklist score unavailable';
}

/**
 * A badge that explains itself in a tooltip. Focusable, so keyboard users reach the tooltip too, and
 * its clicks and Enter/Space stay off the case row, which would otherwise toggle open or closed.
 */
function ExplainedBadge({
  label,
  explanation,
  variant,
  className,
}: {
  label: string;
  explanation: string;
  variant: BadgeProps['variant'];
  className?: string;
}): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={variant}
          className={className}
          tabIndex={0}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ')
              event.stopPropagation();
          }}
        >
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{explanation}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The answer judge's badges for a case's Status cell: the score with the checklist share in its
 * tooltip, a Critical miss flag, or Not scored with the reason. Renders nothing for an unjudged case.
 */
export function AnswerJudgementBadges({
  judgement,
}: {
  judgement: AnswerJudgement | null | undefined;
}): JSX.Element | null {
  const readable = readAnswerJudgement(judgement);
  if (!readable) return null;
  const average = rubricAverageFrom(readable.rubric_average);
  return (
    <div className="flex max-w-full flex-wrap items-center gap-1">
      {readable.status === 'not_scored' ? (
        <ExplainedBadge
          label="Not scored"
          explanation={notScoredText(readable.not_scored_reason)}
          variant="outline"
          className="text-muted-foreground"
        />
      ) : null}
      {readable.status === 'scored' && average !== null ? (
        <ExplainedBadge
          label={`Answer ${averageText(average)}`}
          explanation={checklistText(readable)}
          variant="outline"
        />
      ) : null}
      {readable.critical_miss === true ? (
        <Badge variant="destructive">Critical miss</Badge>
      ) : null}
    </div>
  );
}

/** One scored criterion: its readable name, the 1–5 score and the judge's reason. */
function CriterionRow({
  criterion,
}: {
  criterion: AnswerJudgementCriterion;
}): JSX.Element {
  const name = nonEmptyText(criterion.name);
  const score = wholeNumberInRange(criterion.score, 1, 5);
  const reason = nonEmptyText(criterion.reason);
  return (
    <li
      data-testid="answer-criterion"
      className="rounded-md border border-border bg-muted/30 px-2 py-1 text-xs"
    >
      <div className="flex items-baseline gap-2">
        <span className="font-medium">
          {knownValue(CRITERION_NAMES, name) ??
            (name ? humanize(name) : 'Criterion')}
        </span>
        {score !== null ? (
          <span className="ml-auto shrink-0 tabular-nums">{score}/5</span>
        ) : null}
      </div>
      {reason ? <p className="text-muted-foreground">{reason}</p> : null}
    </li>
  );
}

/** A checklist result as a badge; nothing when the item has no result yet. */
function ChecklistResult({ result }: { result: unknown }): JSX.Element | null {
  const code = nonEmptyText(result);
  if (!code) return null;
  const known = knownValue(CHECKLIST_RESULTS, code);
  return (
    <Badge variant={known?.variant ?? 'outline'}>
      {known?.label ?? humanize(code)}
    </Badge>
  );
}

/** One checklist item: number, text, the critical marker, the judge's result and reason. */
function ChecklistRow({
  item,
}: {
  item: AnswerJudgementChecklistItem;
}): JSX.Element {
  const number = wholeNumberInRange(item.number, 1);
  const reason = nonEmptyText(item.reason);
  return (
    <li
      data-testid="answer-checklist-item"
      className="rounded-md border border-border bg-muted/30 px-2 py-1 text-xs"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        {number !== null ? (
          <span className="tabular-nums text-muted-foreground">{number}.</span>
        ) : null}
        <span className="min-w-0 break-words">{nonEmptyText(item.text)}</span>
        {item.critical === true ? (
          <Badge variant="outline">Critical</Badge>
        ) : null}
        <ChecklistResult result={item.result} />
      </div>
      {reason ? <p className="text-muted-foreground">{reason}</p> : null}
    </li>
  );
}

/** What the judge read, in words; null for `none` or a missing kind. An unknown kind shows as it came. */
export function evidenceKindLabel(value: unknown): string | null {
  const evidence = nonEmptyText(value);
  if (!evidence || evidence === 'none') return null;
  return knownValue(EVIDENCE_KINDS, evidence) ?? humanize(evidence);
}

/** "Evidence: Captured answer · Judge model: … · Agent models: …", leaving out what is missing. */
function judgementFooterText(judgement: AnswerJudgement): string | null {
  const evidenceLabel = evidenceKindLabel(judgement.evidence_kind);
  const judgeModel = nonEmptyText(judgement.judge_model);
  const agentModels = nonEmptyText(judgement.agent_models);
  const parts = [
    evidenceLabel ? `Evidence: ${evidenceLabel}` : null,
    judgeModel ? `Judge model: ${judgeModel}` : null,
    agentModels ? `Agent models: ${agentModels}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The answer judge's full verdict for the expanded case row: why it was not scored, the five
 * criteria, the checklist, and what the judge read with which models. Nothing for an unjudged case.
 */
export function AnswerJudgementDetails({
  judgement,
}: {
  judgement: AnswerJudgement | null | undefined;
}): JSX.Element | null {
  const readable = readAnswerJudgement(judgement);
  if (!readable) return null;
  const criteria = objectsIn<AnswerJudgementCriterion>(readable.criteria);
  const checklist = objectsIn<AnswerJudgementChecklistItem>(readable.checklist);
  const footer = judgementFooterText(readable);
  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs text-muted-foreground/80">Answer quality:</p>
      {readable.status === 'not_scored' ? (
        <p className="text-xs text-muted-foreground">
          {`Not scored: ${notScoredText(readable.not_scored_reason)}`}
        </p>
      ) : null}
      {criteria.length > 0 ? (
        <ul className="space-y-1">
          {criteria.map((criterion, index) => (
            <CriterionRow key={index} criterion={criterion} />
          ))}
        </ul>
      ) : null}
      {checklist.length > 0 ? (
        <>
          <p className="text-xs text-muted-foreground/80">Answer checklist:</p>
          <ul className="space-y-1">
            {checklist.map((item, index) => (
              <ChecklistRow key={index} item={item} />
            ))}
          </ul>
        </>
      ) : null}
      {footer ? (
        <p className="text-xs text-muted-foreground">{footer}</p>
      ) : null}
    </div>
  );
}

/**
 * The summary line's parts, leaving out every value the run lacks or the contract rules out. Critical
 * misses are scored cases, so their count shows only from 1 up to a valid scored count.
 */
function summaryParts(summary: AnswerJudgementSummary): string[] {
  const average = rubricAverageFrom(summary.rubric_average);
  const percent = checklistPercentFrom(summary.checklist_percent);
  const judgedCases = wholeNumberInRange(summary.judged_cases, 0);
  const scoredCases = wholeNumberInRange(summary.scored_cases, 0);
  const criticalMisses = wholeNumberInRange(
    summary.critical_miss_cases,
    1,
    scoredCases ?? 0,
  );
  return [
    average !== null
      ? `Answer quality ${averageText(average)}`
      : 'Answer quality',
    percent !== null ? `Checklist ${percentText(percent)}` : null,
    criticalMisses !== null
      ? `${criticalMisses} critical miss${criticalMisses === 1 ? '' : 'es'}`
      : null,
    judgedCases !== null && scoredCases !== null && scoredCases <= judgedCases
      ? `${scoredCases} of ${judgedCases} scored`
      : null,
  ].filter((part): part is string => part !== null);
}

/**
 * One line under the run summary: answer quality across the run's judged cases, e.g. "Answer
 * quality 4.4/5 · Checklist 67% · 1 critical miss · 2 of 3 scored". Nothing when no case was judged.
 */
export function AnswerJudgementSummaryLine({
  summary,
}: {
  summary: AnswerJudgementSummary | null | undefined;
}): JSX.Element | null {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary))
    return null;
  return (
    <p className="mt-3 text-xs text-muted-foreground">
      {summaryParts(summary).join(' · ')}
    </p>
  );
}
